// The time a scan reveal still holds a contact, from the server's own
// figure (round 084 card 05; ui-map-interface.md §8 "The card family",
// R1837).
//
// The SCANNED chip of a contact's card states how old the scan is, and the
// hull and shield bars fade back to the hatched "?" as the reveal runs
// out. The contact frame carries `scan_left_sec`: the whole map seconds
// the reveal still holds for this reader. The server re-sends a revealed
// contact once a map minute and on every other change, so the client
// counts the figure down between two frames at the tactical scale, the way
// it counts `reload_sec`. A reload, a join or a dropped frame reads the
// same figure as a client that saw the ping.
//
// A new end is taken only when the figure CHANGES. A replay seat view
// supplies the figure of its last mask record with every frame of the
// hull (R922), and that figure stands still while the reveal runs; a live
// frame carries a new figure each map second, so the rule loses nothing.

import { clamp01 } from '../../render/math';

/** What `ScanSeen.observe` reads of one contact of a frame. */
export interface SeenFleet {
  id: number;
  /** Whole map seconds the reveal still holds; 0 with no reveal. */
  scanLeftSec: number;
}

/** One reveal: the figure last read and the wall ms at which it ends. */
interface Reveal {
  sec: number;
  endMs: number;
}

/** The end of each contact's reveal, as the server stated it. */
export class ScanSeen {
  private readonly at = new Map<number, Reveal>();
  /** Map seconds per wall second, at the last frame. */
  private scale = 1;

  /**
   * One State frame. `fleets` are the contacts the frame carries,
   * `removed` the ids it drops, `full` whether it replaces the picture,
   * and `mapPerWall` the tactical scale (map seconds per wall second).
   */
  observe(fleets: readonly SeenFleet[], removed: readonly number[], full: boolean,
    nowMs: number, mapPerWall: number): void {
    this.scale = mapPerWall > 0 ? mapPerWall : 1;
    if (full) {
      const keep = new Set<number>();
      for (const f of fleets) keep.add(f.id);
      // A Map keeps its iteration valid across a delete of the current key.
      for (const id of this.at.keys()) if (!keep.has(id)) this.at.delete(id);
    }
    for (const id of removed) this.at.delete(id);
    for (const f of fleets) {
      if (!(f.scanLeftSec > 0)) {
        this.at.delete(f.id);
        continue;
      }
      const had = this.at.get(f.id);
      if (had?.sec === f.scanLeftSec) continue;
      this.at.set(f.id, { sec: f.scanLeftSec, endMs: nowMs + f.scanLeftSec / this.scale * 1000 });
    }
  }

  /** Map seconds the reveal of a contact still holds (0 at the end), or
   *  null when no frame stated a reveal. */
  leftSec(id: number, nowMs: number): number | null {
    const r = this.at.get(id);
    if (!r) return null;
    return Math.max(0, (r.endMs - nowMs) / 1000 * this.scale);
  }
}

/**
 * What the contact card shows of one reveal: the age of the scan in map
 * seconds, and the fill of the hull and shield bars from 1 (fresh) to 0
 * (the hatched "?"). `revealSec` is the whole reveal (the catalog rule
 * `scan_reveal_sec`), and `fadeShare` the last share of it over which the
 * bars fade (`[presentation.scan]`). With no figure, or no rule, the chip
 * states no age and the bars do not fade.
 */
export function scanLook(leftSec: number | null, revealSec: number, fadeShare: number):
  { ageSec: number | null; fade: number } {
  if (leftSec === null || !(revealSec > 0)) return { ageSec: null, fade: 1 };
  const ageSec = Math.max(0, revealSec - leftSec);
  const window = revealSec * fadeShare;
  const fade = window > 0 ? clamp01(leftSec / window) : 1;
  return { ageSec, fade };
}
